import React, { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import PostStatusBadge from './PostStatusBadge';

const ACTION_LABELS = {
  approved: 'Aprovado',
  rejected: 'Rejeitado',
  revision_requested: 'Revisao Solicitada',
};

const ACTION_ICONS = {
  approved: '\u2705',
  rejected: '\u274C',
  revision_requested: '\uD83D\uDD04',
};

function ApprovalPanel({ post, approvals, loading, onApprove, onReject, onRequestRevision, clientId }) {
  const { profile } = useAuth();
  const { hasPermission } = usePermissions();
  const reviewerName = profile?.full_name || profile?.email || '';
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const canApprove = hasPermission(clientId, 'approvals', 'approve');

  const handleAction = async (actionFn) => {
    if (!reviewerName) return;
    setSubmitting(true);
    try {
      await actionFn(reviewerName, notes.trim());
      setNotes('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* Post info */}
      <div className="approval-panel">
        <div className="approval-header">
          <div>
            <h3 style={{ fontSize: '1rem', marginBottom: '4px' }}>
              {post.workingTitle || post.title}
            </h3>
            <div className="calendar-post-meta">
              <span>{post.format}</span>
              <PostStatusBadge status={post.status || 'draft'} />
            </div>
          </div>
        </div>

        {/* Approval form */}
        <div style={{ marginTop: 'var(--spacing-md)' }}>
          <div className="form-group">
            <label className="form-label">Revisor</label>
            <input
              className="form-input"
              value={reviewerName}
              disabled
              style={{ opacity: 0.7 }}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Notas / Observacoes</label>
            <textarea
              className="form-textarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Comentarios sobre o post..."
              style={{ minHeight: '80px' }}
              disabled={!canApprove}
            />
          </div>
          {canApprove && (
            <div className="approval-actions">
              <button
                className="btn btn-primary"
                disabled={submitting}
                onClick={() => handleAction(onApprove)}
              >
                Aprovar
              </button>
              <button
                className="btn btn-secondary"
                disabled={submitting}
                onClick={() => handleAction(onRequestRevision)}
              >
                Solicitar Revisao
              </button>
              <button
                className="btn btn-danger"
                disabled={submitting}
                onClick={() => handleAction(onReject)}
              >
                Rejeitar
              </button>
            </div>
          )}
          {!canApprove && (
            <p style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem', marginTop: 'var(--spacing-sm)' }}>
              Voce nao tem permissao para aprovar/rejeitar este post.
            </p>
          )}
        </div>
      </div>

      {/* Approval history */}
      <div className="approval-history">
        <h4 style={{ fontSize: '0.95rem', marginBottom: 'var(--spacing-md)', color: 'var(--color-text-secondary)' }}>
          Historico ({approvals.length})
        </h4>
        {loading ? (
          <div className="loading">
            <div className="loading-spinner" />
            Carregando...
          </div>
        ) : approvals.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>
            Nenhuma aprovacao registrada para este post.
          </p>
        ) : (
          approvals.map(a => (
            <div key={a.id} className="approval-entry">
              <div className="approval-entry-icon">
                {ACTION_ICONS[a.action] || '?'}
              </div>
              <div className="approval-entry-info">
                <div className="approval-entry-action">
                  {ACTION_LABELS[a.action] || a.action} por {a.reviewerName}
                </div>
                <div className="approval-entry-meta">
                  {new Date(a.createdAt).toLocaleString('pt-BR')}
                </div>
                {a.notes && (
                  <div className="approval-entry-notes">{a.notes}</div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default ApprovalPanel;
